import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Box, Button, FormControl, InputLabel, Input, FormGroup, FormHelperText, CircularProgress } from '@mui/material';
import adminManagementService from '../../../services/AdminManagementService';
import ValidationService from '../../../services/ValidationService'
import { emptyAdminProfile } from '../fixtures';
import { Admin } from '../../../interfaces/Admin';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../styles';

function getModalStyle() {
    return {
        top: `35vh`,
        left: `37.5vw`,
    };
}

interface EditAdminPasswordModalProps {
    open: boolean;
    handleClose: () => void;
    profile: Admin;
}

export default function EditAdminPasswordModal(props: EditAdminPasswordModalProps) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [modalStyle] = useState(getModalStyle);
    const [adminPassword, setAdminPassword] = useState("");
    const [profile, setProfile] = useState<Admin>(emptyAdminProfile);
    const [hasError, setErr] = useState(false);
    const [hasSucess, setSuccess] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState('');

    useEffect(() => {
        setProfile(props.profile);
    }, [props.profile]);

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            submitForm();
        }
    };

    const submitForm = async () => {
        setLoading(true);
        setPasswordValidation('');
        setErr(false);
        setSuccess(false);
        const passwordError = await ValidationService.getPasswordValidationError(adminPassword);
        let success = false;
        if (!passwordError) {
            success = await adminManagementService.editAdminPassword(adminPassword, profile.id!);
        }
        setLoading(false);
        if (success) setSuccess(true);
        else {
            setPasswordValidation(passwordError ? passwordError : '');
            setErr(true);
        }
    };
    const handleAdminPassChange = (e: any) => {
        e.persist();
        setPasswordValidation('');
        setErr(false);
        setSuccess(false);
        const value = e.target.value.replace(/\s/g, '');
        setAdminPassword(value);
    }

    const passwordInputProps = {
        'data-testid': 'input-password',
    };
    
    return (
        <Modal
            aria-labelledby="admin-modal-title"
            aria-describedby="admin-modal-description"
            data-cy="admin-edit-password"
            open={props.open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    props.handleClose();
                }
            }}
        >
            <Box sx={[styles.paper, modalStyle]}>
                <h2 id="admin-modal-title">Change password for {profile?.username ? profile.username : " "}</h2>
                <p id="admin-modal-description">Enter the new password below</p>
                <FormGroup>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="password">Admin's new password</InputLabel>
                        <Input 
                            onChange={handleAdminPassChange} 
                            value={adminPassword} 
                            id="password" 
                            aria-describedby="admin password" 
                            type='password' 
                            data-cy='admin-edit-password' 
                            onKeyPress={handleKeyPress} 
                            inputProps={passwordInputProps}
                        />
                        {hasError ? <FormHelperText sx={styles.redTxt}>{passwordValidation || 'Unable to change password'}</FormHelperText> : undefined}
                        {hasSucess ? <FormHelperText sx={styles.greenTxt}>Password changed successfully.</FormHelperText> : undefined}
                    </FormControl>
                </FormGroup>
                <FormGroup sx={[styles.mt2, styles.dfr]}>
                    <Button data-cy='change-password-confirm' variant="contained" color="secondary" disabled={!adminPassword.length} onClick={submitForm}>Change password</Button>
                    <Button
                        data-cy='change-password-close'
                        variant="contained"
                        onClick={props.handleClose}
                        sx={styles.ml2}>Close</Button>
                </FormGroup>
                {isLoading ? <CircularProgress color="primary" /> : undefined}
            </Box>
        </Modal>
    );
}